import { useEffect, useState } from "react";

export function useHasKeyboard() {
  const [hasKeyboard, setHasKeyboard] = useState<boolean>(() => detectHasKeyboard());

  useEffect(() => {
    // Update dynamically if something changes (rare but possible)
    const mqlFine = window.matchMedia("(any-pointer: fine)");
    const mqlCoarse = window.matchMedia("(any-pointer: coarse)");

    const update = () => setHasKeyboard(detectHasKeyboard());

    mqlFine.addEventListener("change", update);
    mqlCoarse.addEventListener("change", update);

    // Key press as final confirmation (hybrids)
    const handleKeyDown = () => setHasKeyboard(true);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      mqlFine.removeEventListener("change", update);
      mqlCoarse.removeEventListener("change", update);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return hasKeyboard;
}

export function detectHasKeyboard(): boolean {
  const touchPoints = navigator.maxTouchPoints || 0;
  const hasFinePointer = window.matchMedia("(any-pointer: fine)").matches;
  const hasCoarsePointer = window.matchMedia("(any-pointer: coarse)").matches;
  const platform = navigator.platform || "";
  const ua = navigator.userAgent || "";

  // Detect iPadOS pretending to be Mac
  const isIOSKeyboardCapable =
    platform === "MacIntel" && touchPoints > 1;

  // Detect Android / iOS touch-only devices
  const isLikelyMobile =
    /Android|iPhone|iPod/.test(ua) ||
    (platform === "MacIntel" && touchPoints > 1); // iPadOS case

  // Core heuristic:
  // - fine pointer (mouse/trackpad) ⇒ keyboard available
  // - coarse pointer only ⇒ probably touch-only
  // - iPad with Magic Keyboard triggers fine pointer
  // - fallback: assume keyboard on desktops
  if (isIOSKeyboardCapable || hasFinePointer) return true;
  if (hasCoarsePointer && touchPoints > 0 && !isIOSKeyboardCapable) return false;

  // Default assumption: desktop = keyboard
  return !isLikelyMobile;
}